import { columnLabel, rowLabel, type StageRect } from '../../lib/geometry';
import styles from './GridOverlay.module.css';

/**
 * {@link rulerLayout}'s output, converted to stage pixels and expressed relative to the
 * board's own box so it can drive absolutely-positioned children.
 *
 * The conversion lives in `Stage`; this type only exists so the two cannot drift.
 */
export interface StageRulerLayout {
  /** Font size, stage px. */
  readonly rf: number;
  /** Baseline for the column letters, stage px from the board's top edge. */
  readonly columnY: number;
  /** Text anchor for the row numbers, stage px from the board's left edge. */
  readonly rowX: number;
  readonly rowsFlipped: boolean;
}

interface Props {
  /** Where the board sits on the stage, in stage px. */
  rect: StageRect;
  /** Divisions per side. */
  n: number;
  /** Gridline and ruler colour. */
  lineColor: string;
  /** Shared with the export, so the editor previews the sheet it will produce (ADR-0012). */
  ruler: StageRulerLayout;
}

/**
 * Where Inter's alphabetic baseline sits inside a `line-height: 1` box, as a fraction of em.
 *
 * Inter's ascender is ~0.969em and its descender ~0.241em, so the content area is 1.21em.
 * At `line-height: 1` the half-leading is `(1 - 1.21) / 2 = -0.105em`, putting the baseline
 * at `0.969 - 0.105`. Canvas draws the letters on that same alphabetic baseline, so this is
 * what converts between the two.
 */
const BASELINE_FROM_TOP = 0.864;

/**
 * The board's border, in px — must match `.board` in the stylesheet.
 *
 * Absolutely-positioned children are placed against the **padding** box, while the ruler
 * geometry is expressed against the border box (the gridlines use `background-origin:
 * border-box`, and the cells are `size / n` of the border box). Offsetting by the border
 * keeps the rulers on the same grid as the cells they label — without it every label drifts,
 * and the ruler bands span `size - 2 * border`, so the drift grows across the board.
 */
const BORDER = 2;

/**
 * The board drawn over the picture: frame, gridlines, corner handles and the two rulers.
 *
 * **The gridlines are a `repeating-linear-gradient` on a `border-box` element.** The
 * `background-origin`/`background-clip` pair in the stylesheet is load-bearing: without it
 * the gradient repeats over the padding box, which is 4px smaller than the border box, and
 * the board renders 8.86 cells instead of 9. See the handoff and README.
 */
export function GridOverlay({ rect, n, lineColor, ruler }: Props): React.JSX.Element {
  const cell = rect.size / n;

  return (
    <div
      className={styles.board}
      style={{
        left: `${rect.x}px`,
        top: `${rect.y}px`,
        width: `${rect.size}px`,
        height: `${rect.size}px`,
        borderColor: lineColor,
        backgroundImage: [
          `repeating-linear-gradient(to right, ${lineColor} 0 1px, transparent 1px ${cell}px)`,
          `repeating-linear-gradient(to bottom, ${lineColor} 0 1px, transparent 1px ${cell}px)`,
        ].join(','),
      }}
    >
      <div
        className={styles.columnRuler}
        style={{
          gridTemplateColumns: `repeat(${n}, 1fr)`,
          color: lineColor,
          fontSize: `${ruler.rf.toFixed(2)}px`,
          // Span the border box, so the n columns line up with the n cells.
          left: `${-BORDER}px`,
          right: `${-BORDER}px`,
          top: `${(ruler.columnY - ruler.rf * BASELINE_FROM_TOP - BORDER).toFixed(2)}px`,
        }}
        aria-hidden="true"
      >
        {Array.from({ length: n }, (_, i) => (
          <div key={i}>{columnLabel(i)}</div>
        ))}
      </div>

      <div
        className={styles.rowRuler}
        data-flipped={ruler.rowsFlipped}
        style={{
          gridTemplateRows: `repeat(${n}, 1fr)`,
          color: lineColor,
          fontSize: `${ruler.rf.toFixed(2)}px`,
          top: `${-BORDER}px`,
          bottom: `${-BORDER}px`,
          // rowX is the text anchor: the right edge of the labels normally, their left edge
          // when flipped inside the board.
          ...(ruler.rowsFlipped
            ? { left: `${(ruler.rowX - BORDER).toFixed(2)}px`, right: 'auto' }
            : { right: `${(rect.size - ruler.rowX - BORDER).toFixed(2)}px`, left: 'auto' }),
        }}
        aria-hidden="true"
      >
        {Array.from({ length: n }, (_, i) => (
          <div key={i}>{rowLabel(i)}</div>
        ))}
      </div>

      <span className={`${styles.handle} ${styles.nw}`} data-handle="nw" />
      <span className={`${styles.handle} ${styles.ne}`} data-handle="ne" />
      <span className={`${styles.handle} ${styles.sw}`} data-handle="sw" />
      <span className={`${styles.handle} ${styles.se}`} data-handle="se" />
    </div>
  );
}
